import { z } from 'zod';
import { CHANNEL_PROVENANCE, describeAnswer } from './answers.js';
import { CHAT_MESSAGE_MAX, chatPresentation, isMultiSelect, needsOwnConfirmation, quickReplies, } from './chat.js';
import { checkAnswer, fromAgentValue, toAgentQuestion, } from './questions.js';
/**
 * The conversation module's rules (tracker task 25): which questions the chat asks and in what order,
 * which agent proposals become confirmation-card rows, and what the assistant says itself. The core keeps
 * the sessions, turns and transcript; docs/design/chat-ux.md describes the behaviour.
 */
export const CONVERSATION_LANGUAGES = ['en', 'ms'];
/** How many questions the agent sees in one turn: the one asked now and the next few it may also answer. */
export const AGENT_OPEN_QUESTIONS = 10;
// ---------- requests ----------
const QuestionKey = z.string().min(1).max(64);
export const StartConversation = z.strictObject({
    language: z.enum(CONVERSATION_LANGUAGES).optional(),
});
export const ChangeLanguage = z.strictObject({ language: z.enum(CONVERSATION_LANGUAGES) });
/** Typed words: read by the conversation agent, shown back on a card before anything is saved. */
export const SendMessage = z.strictObject({ text: z.string().trim().min(1).max(CHAT_MESSAGE_MAX) });
/** A tapped quick reply or a saved inline form card: the person's own answer, saved at once. */
export const AnswerLive = z.strictObject({
    question: QuestionKey,
    value: z.unknown().refine((v) => v !== undefined && v !== null, 'An answer is needed'),
    via: z.enum(['tapped', 'form']),
});
export const DecideRows = z.strictObject({ questions: z.array(QuestionKey).min(1).max(10) });
export const SkipQuestion = z.strictObject({ question: QuestionKey });
const seqOf = (id) => Number(id.slice(1));
/**
 * The transcript after an action: changed items replaced where they were, new ones added, in order. A
 * superseded action changed nothing. Pure, so a screen can apply each update to what it shows.
 */
export function applyUpdate(view, update) {
    if (update.turn?.status === 'superseded')
        return view;
    const byId = new Map(view.items.map((it) => [it.id, it]));
    for (const it of update.items)
        byId.set(it.id, it);
    return {
        ...view,
        conversation: update.conversation,
        items: [...byId.values()].sort((a, b) => seqOf(a.id) - seqOf(b.id)),
        live: update.live,
        progress: update.progress,
    };
}
// ---------- the question queue ----------
/** Asked before optional questions: mandatory ones, and follow-up probes once their weak answer opens them. */
export const pressing = (q) => q.mandatory || !!q.metadata.probe_of;
const askable = (display, key, answered) => {
    const s = display[key];
    return !answered && s?.state === 'visible' && s.ask;
};
/**
 * The questions the chat and the form ask, in order: mandatory questions (and opened follow-up probes) of
 * the open stages in journey order, then the optional ones. Questions skipped in this conversation and
 * questions chat can't present (derived ones) are left out. Without a journey: every question asked now,
 * mandatory first, in configuration order.
 */
export function questionQueue(inp) {
    const skipped = new Set(inp.skipped ?? []);
    const ok = (k) => k in inp.questions &&
        !skipped.has(k) &&
        chatPresentation(inp.questions[k]) !== 'not_asked' &&
        askable(inp.display, k, inp.answered(k));
    let ordered;
    if (inp.journey) {
        const j = inp.journey;
        const rank = new Map((inp.ranked ?? []).map((k, i) => [k, i]));
        ordered = j.stages.flatMap((s) => {
            const st = inp.stages[s.key];
            const keys = (st?.questions ?? []).filter((q) => j.questions[q]?.stage === s.key && j.questions[q].open);
            if (st?.order !== 'ranked' || rank.size === 0)
                return keys;
            const pos = (k, i) => rank.get(k) ?? rank.size + i;
            return keys
                .map((k, i) => [k, pos(k, i)])
                .sort((a, b) => a[1] - b[1])
                .map(([k]) => k);
        });
    }
    else
        ordered = Object.keys(inp.questions);
    const asked = ordered.filter(ok);
    return [
        ...asked.filter((k) => pressing(inp.questions[k])),
        ...asked.filter((k) => !pressing(inp.questions[k])),
    ];
}
/**
 * The transcript item that asks a question, with how chat presents it. With a phrasing (the phrasing
 * agent's wording in the conversation's language), its text, why and quick-reply labels are used; values
 * always stay as configured. Without one: the configured text, and the help's first sentence as the why.
 */
export function questionItem(key, q, stage, again = false, phrasing) {
    const p = chatPresentation(q);
    const labels = new Map((phrasing?.options ?? []).map((o) => [o.value, o.label]));
    return {
        kind: 'question',
        question: key,
        stage,
        text: phrasing?.text || q.text,
        why: phrasing?.why || (q.help.match(/^[^.!?]*[.!?]/)?.[0] ?? q.help).slice(0, 200),
        presentation: p === 'not_asked' ? 'inline_form' : p,
        quick_replies: p === 'quick_replies'
            ? quickReplies(q).map((r) => ({ value: r.value, label: labels.get(r.value) || r.label }))
            : [],
        multiple: isMultiSelect(q),
        kill_switch: q.metadata.kill_switch,
        optional: !q.mandatory,
        again,
    };
}
/** The open questions for one agent turn: the one asked now first, then the next few the agent can read. */
export function agentQuestions(questions, current, queue) {
    const out = [];
    for (const k of [current, ...queue.filter((x) => x !== current)]) {
        const q = questions[k];
        const a = q && toAgentQuestion(k, q);
        if (a)
            out.push(a);
        else if (k === current)
            return [];
        if (out.length === AGENT_OPEN_QUESTIONS)
            break;
    }
    return out;
}
/**
 * Checks the agent's proposals the way the answer engine will: an open question, a valid value of its
 * type, and at least the question's chat confidence. The agent only proposes; this decides what the
 * person is asked to confirm. Rows follow the order of `open` (the question asked now first).
 */
export function proposalRows(questions, open, proposals) {
    const provenance = CHANNEL_PROVENANCE.chat[0];
    const byQuestion = new Map();
    const rejected = [];
    for (const p of proposals) {
        const key = p.target.startsWith('question:') ? p.target.slice('question:'.length) : null;
        const q = key ? questions[key] : undefined;
        const no = (reason) => rejected.push({ target: p.target, reason });
        if (!key || !q || !open.includes(key)) {
            no('not an open question');
            continue;
        }
        if (byQuestion.has(key)) {
            no('a second answer to the same question');
            continue;
        }
        if (p.confidence < q.metadata.chat_min_confidence) {
            no(`confidence ${p.confidence} is below ${q.metadata.chat_min_confidence}`);
            continue;
        }
        const check = checkAnswer(q, fromAgentValue(q, p.value), provenance);
        if (!check.ok) {
            no(`not a valid answer: ${check.errors.map((e) => e.message).join('; ')}`);
            continue;
        }
        const ev = p.evidence.find((e) => e.kind === 'chat_message');
        byQuestion.set(key, {
            question: key,
            question_text: q.text,
            value: check.value,
            label: describeAnswer(q, check.value),
            confidence: p.confidence,
            quote: ev?.quote ? ev.quote.slice(0, 1000) : null,
            own_confirmation: needsOwnConfirmation(q),
            kill_switch: q.metadata.kill_switch,
        });
    }
    const rows = open.filter((k) => byQuestion.has(k)).map((k) => byQuestion.get(k));
    for (const r of rows.slice(10))
        rejected.push({ target: `question:${r.question}`, reason: 'more than 10 on one card' }); // prettier-ignore
    return { rows: rows.slice(0, 10), rejected };
}
// ---------- what the assistant says itself ----------
/**
 * The assistant's own lines, in both launch languages (decision 0007). The Malay lines need review by a
 * native speaker before launch, like the question translations.
 */
export const CHAT_COPY = {
    en: {
        greeting: 'Hi. I’ll ask a few questions, one at a time. Tap an answer, or tell me in your own words and I’ll check I’ve understood before saving anything.',
        welcome_back: 'Welcome back. Let’s carry on.',
        understood: 'Here’s what I understood.',
        saved: (n) => (n > 1 ? `Saved ${n} answers.` : 'Saved.'),
        declined: 'No problem, nothing was saved.',
        skip: 'Skip',
        skipped: 'Skipped. You can come back to it from the assessment page.',
        no_match: 'I couldn’t match that to an answer. Which of these is closest?',
        form_only: 'This one is easier to answer with the options below.',
        unavailable: 'I can’t read typed replies right now. Tap an answer or use the form instead.',
        all_done: 'That’s everything for now. You can still change an answer from the assessment page.',
        enough: 'That’s enough for a reliable score. The questions left are optional: each one you answer sharpens it, or you can stop here.',
        insurer_asks: 'Your insurer asked you to look at this answer again:',
        changes_done: 'That’s every change your insurer asked for. Send it back to them from Home when you’re ready.',
    },
    ms: {
        greeting: 'Hai. Saya akan bertanya beberapa soalan, satu demi satu. Tekan jawapan, atau beritahu saya dengan kata-kata anda sendiri dan saya akan semak dahulu sebelum menyimpan apa-apa.',
        welcome_back: 'Selamat kembali. Mari kita teruskan.',
        understood: 'Ini yang saya faham.',
        saved: (n) => (n > 1 ? `${n} jawapan disimpan.` : 'Disimpan.'),
        declined: 'Tiada masalah, tiada apa-apa yang disimpan.',
        skip: 'Langkau',
        skipped: 'Dilangkau. Anda boleh kembali kepadanya dari halaman penilaian.',
        no_match: 'Saya tidak dapat padankan itu dengan jawapan. Yang mana paling hampir?',
        form_only: 'Soalan ini lebih mudah dijawab dengan pilihan di bawah.',
        unavailable: 'Saya tidak dapat membaca balasan bertaip sekarang. Tekan jawapan atau gunakan borang.',
        all_done: 'Itu sahaja buat masa ini. Anda masih boleh menukar jawapan dari halaman penilaian.',
        enough: 'Itu sudah cukup untuk skor yang boleh dipercayai. Soalan yang tinggal adalah pilihan: setiap jawapan menajamkannya, atau anda boleh berhenti di sini.',
        insurer_asks: 'Penanggung insurans anda meminta anda menyemak semula jawapan ini:',
        changes_done: 'Itu semua perubahan yang diminta oleh penanggung insurans anda. Hantar semula kepada mereka dari Laman Utama apabila anda bersedia.',
    },
};
//# sourceMappingURL=conversation.js.map